/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { Download, Eye } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api, isApiError } from '../../api';
import type { DocumentDetail } from '../../api/areas/accounts';
import { DataTable, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { DOCUMENT_CODE, dossierFileName, extensionOf, OTHER_CODE } from '../../lib/dossierFileNames';
import { formatDate, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { FileViewer, type FileTarget } from '../declarations/shared';
import { OwnerLink } from './OwnerLink';
import { CLASSIFICATION, DOC_VERIFICATION, DOSSIER_STATUS, EnumBadge, EnumText, formatBytes, mutedSmall, paths, Stack, useEnumLabel } from './shared';

type Version = DocumentDetail['versions'][number];
type Binding = DocumentDetail['bindings'][number];

/** The name a version is saved as: its dossier file name when a dossier holds it (as the server names it), else `<type>-v<n>.<ext>`. */
function fileNameOf(d: DocumentDetail, v: Version): string {
  const b = d.bindings.find((x) => x.document_version_id === v.id);
  const ext = extensionOf(v.mime_type);
  return b ? dossierFileName(b.ascii_name, DOCUMENT_CODE[b.purpose] ?? DOCUMENT_CODE[d.document_type] ?? OTHER_CODE, ext) : `${d.document_type.toLowerCase().replace(/_/g, '-')}-v${v.version_number}.${ext}`;
}

/** "Xem" / "Tải về" one version (identity_document.view and a reason, audited). */
function VersionActions({ onOpen, size = 'icon' }: { onOpen: (download: boolean) => void; size?: 'icon' | 'sm' }) {
  const { t } = useTranslation('accounts');
  return size === 'sm' ? (
    <span className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" onClick={() => onOpen(false)} data-document-view>
        <Eye /> {t('document.view')}
      </Button>
      <Button size="sm" variant="outline" onClick={() => onOpen(true)} data-document-download>
        <Download /> {t('document.download')}
      </Button>
    </span>
  ) : (
    <span className="flex items-center gap-0.5">
      <Button variant="ghost" size="icon" className="size-7" aria-label={t('document.view')} title={t('document.view')} onClick={() => onOpen(false)}>
        <Eye className="size-3.5" />
      </Button>
      <Button variant="ghost" size="icon" className="size-7" aria-label={t('document.download')} title={t('document.download')} onClick={() => onOpen(true)}>
        <Download className="size-3.5" />
      </Button>
    </span>
  );
}

/** `/documents/:id`: metadata, versions (each viewable / downloadable with a reason), verification outcomes and dossier bindings. */
export function DocumentPage() {
  const { t } = useTranslation('accounts');
  const label = useEnumLabel('accounts');
  const { id = '' } = useParams();
  const [viewing, setViewing] = useState<(FileTarget & { version: string }) | null>(null);
  const result = useApi((signal) => api.getDocument(id, { signal }), [id]);
  const title = result.status === 'success' ? label('documentType', result.data.document_type) : t('document.fallbackTitle');
  useDocumentTitle(title);

  let body;
  if (result.status === 'loading') body = <Loading label={t('document.loading')} rows={4} />;
  else if (result.status === 'error') {
    const notFound = isApiError(result.error) && result.error.status === 404;
    body = <DataError error={result.error} onRetry={notFound ? undefined : result.reload} title={notFound ? t('document.notFound') : t('document.loadError')} />;
  } else {
    const d = result.data;
    const open = (v: Version, download: boolean) => setViewing({ version: v.id, name: fileNameOf(d, v), download });
    const current = d.versions.find((v) => v.id === d.current_version_id) ?? d.versions[0] ?? null;
    const versionColumns: Column<Version>[] = [
      {
        key: 'version',
        header: t('document.versionCol.version'),
        numeric: true,
        cell: (v) => (
          <>
            {t('common.versionN', { n: v.version_number })}
            {v.id === d.current_version_id ? <div className={mutedSmall}>{t('document.current')}</div> : null}
          </>
        ),
      },
      {
        key: 'file',
        header: t('document.versionCol.file'),
        cell: (v) => (
          <div className="text-xs">
            {v.mime_type}
            <div className="text-muted-foreground">{formatBytes(v.size_bytes)}</div>
          </div>
        ),
      },
      { key: 'sha', header: t('document.versionCol.sha'), cell: (v) => <CopyText value={v.sha256} display={`${v.sha256.slice(0, 12)}…`} /> },
      { key: 'issued', header: t('document.versionCol.issued'), className: 'whitespace-nowrap', cell: (v) => formatDate(v.issued_at) },
      { key: 'expires', header: t('document.versionCol.expires'), className: 'whitespace-nowrap', cell: (v) => formatDate(v.expires_at) },
      {
        key: 'uploaded',
        header: t('document.versionCol.uploaded'),
        className: 'whitespace-normal',
        cell: (v) => (
          <div className="min-w-40 max-w-56">
            <span className="whitespace-nowrap">{formatDateTime(v.uploaded_at)}</span>
            <div className={mutedSmall}>{t('document.by', { actor: v.uploaded_by_name ? `${v.uploaded_by_name} (${label('actorType', v.uploaded_by_actor_type)})` : label('actorType', v.uploaded_by_actor_type) })}</div>
          </div>
        ),
      },
      {
        key: 'verifications',
        header: t('document.versionCol.verifications'),
        className: 'whitespace-normal',
        cell: (v) =>
          v.verifications.length ? (
            <ul className="min-w-56 max-w-80 space-y-2">
              {v.verifications.map((q) => (
                <li key={q.id} className="space-y-0.5">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <EnumBadge group="docVerification" tones={DOC_VERIFICATION} value={q.status} />
                    <EnumText group="verificationType" value={q.verification_type} />
                  </span>
                  <span className={`block ${mutedSmall}`}>
                    {[q.provider, q.score ? t('document.score', { score: q.score }) : null, q.reviewer_name ? t('document.reviewedBy', { name: q.reviewer_name }) : null, formatDateTime(q.verified_at ?? q.created_at)]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-muted-foreground">{t('common.none')}</span>
          ),
      },
      { key: 'open', header: '', className: 'whitespace-nowrap', cell: (v) => <VersionActions onOpen={(download) => open(v, download)} /> },
    ];
    const bindingColumns: Column<Binding>[] = [
      {
        key: 'domain',
        header: t('document.bindingCol.domain'),
        cell: (b) => <EntityRef to={paths.domain(b.ascii_name)} id={b.domain_id} label={b.unicode_name} sub={b.unicode_name !== b.ascii_name ? b.ascii_name : undefined} />,
      },
      {
        key: 'dossier',
        header: t('document.bindingCol.dossier'),
        cell: (b) => (
          <span className="inline-flex items-center gap-2">
            <EntityRef to={paths.dossier(b.dossier_id)} id={b.dossier_id} label={t('common.versionN', { n: b.dossier_version })} />
            <EnumBadge group="dossierStatus" tones={DOSSIER_STATUS} value={b.dossier_status} />
          </span>
        ),
      },
      { key: 'purpose', header: t('document.bindingCol.purpose'), cell: (b) => <EnumText group="purpose" value={b.purpose} /> },
      {
        key: 'version',
        header: t('document.bindingCol.version'),
        cell: (b) => {
          const v = d.versions.find((x) => x.id === b.document_version_id);
          return v ? t('common.versionN', { n: v.version_number }) : '—';
        },
      },
      { key: 'bound', header: t('document.bindingCol.bound'), className: 'whitespace-nowrap', cell: (b) => formatDateTime(b.bound_at) },
    ];
    body = (
      <Stack>
        <Section title={t('document.section')}>
          <div className="space-y-4">
            <KeyValue
              columns={3}
              rows={[
                [t('document.field.type'), <span key="t" title={d.document_type}>{label('documentType', d.document_type)}</span>],
                [t('document.field.classification'), <EnumBadge key="c" group="classification" tones={CLASSIFICATION} value={d.classification} />],
                [t('document.field.owner'), <OwnerLink key="o" type={d.owner_type} id={d.owner_id} name={d.owner_name} />],
                [t('document.field.versions'), d.versions.length],
                [t('document.field.created'), formatDateTime(d.created_at)],
                [t('common.updated'), formatDateTime(d.updated_at)],
                [t('common.id'), <CopyText key="id" value={d.id} />],
              ]}
            />
            {current ? <VersionActions size="sm" onOpen={(download) => open(current, download)} /> : null}
          </div>
        </Section>

        <Tabs defaultValue="versions">
          <TabsList className="max-w-full flex-wrap justify-start group-data-[orientation=horizontal]/tabs:h-auto">
            <TabsTrigger value="versions">{t('document.tab.versions', { count: d.versions.length })}</TabsTrigger>
            <TabsTrigger value="bindings">{t('document.tab.bindings', { count: d.bindings.length })}</TabsTrigger>
          </TabsList>
          <TabsContent value="versions">
            <Section flush>
              <DataTable columns={versionColumns} rows={d.versions} rowKey={(v) => v.id} empty={<EmptyState title={t('document.noVersions')} />} />
            </Section>
          </TabsContent>
          <TabsContent value="bindings">
            <Section flush>
              <DataTable columns={bindingColumns} rows={d.bindings} rowKey={(b) => b.binding_id} empty={<EmptyState title={t('document.noBindings')} />} />
            </Section>
          </TabsContent>
        </Tabs>
      </Stack>
    );
  }

  return (
    <>
      <PageHeader
        title={title}
        subtitle={result.status === 'success' && result.data.owner_name ? result.data.owner_name : undefined}
        crumbs={[{ label: t('crumb') }, { label: t('documents.title'), to: '/documents' }, { label: title }]}
      />
      {body}
      <FileViewer
        target={viewing}
        onClose={() => setViewing(null)}
        title={t('document.readTitle', { name: viewing?.name ?? '' })}
        description={t('document.readBody')}
        hint={t('document.previewHint')}
        downloadLabel={t('document.download')}
        marker="document-file-preview"
        load={(reason, f) => api.getDocumentContent(id, viewing?.version ?? '', { reason, download: f.download })}
      />
    </>
  );
}
